import * as React from "react";
import { format, isValid, parse } from "date-fns";
import { CalendarDays, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const ISO = "yyyy-MM-dd";

/** Parses a `yyyy-MM-dd` string as a local date (no UTC shift). */
function fromIso(value?: string) {
  if (!value) return undefined;
  const d = parse(value, ISO, new Date());
  return isValid(d) ? d : undefined;
}

interface DatePickerProps {
  /** Selected date as `yyyy-MM-dd`, the same shape a native date input uses. */
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Earliest / latest selectable day, as `yyyy-MM-dd`. */
  min?: string;
  max?: string;
  disabled?: boolean;
  required?: boolean;
  /** Show a clear button when a date is set (off for required fields). */
  clearable?: boolean;
  /** Month/year dropdowns in the header, for dates far from today such as a birthday. */
  captionLayout?: "label" | "dropdown";
  id?: string;
  className?: string;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
}

/** A button that opens the shadcn Calendar in a popover. Drop-in for `<Input type="date">`. */
export function DatePicker({
  value,
  onChange,
  placeholder = "Pick a date",
  min,
  max,
  disabled,
  required,
  clearable = !required,
  captionLayout = "label",
  id,
  className,
  ...aria
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false);
  const selected = fromIso(value);
  const minDate = fromIso(min);
  const maxDate = fromIso(max);

  const disabledDays = [
    ...(minDate ? [{ before: minDate }] : []),
    ...(maxDate ? [{ after: maxDate }] : []),
  ];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className={cn("relative", className)}>
        <PopoverTrigger asChild>
          <button
            type="button"
            id={id}
            disabled={disabled}
            aria-haspopup="dialog"
            aria-required={required || undefined}
            {...aria}
            className={cn(
              "flex h-10 w-full items-center gap-2 rounded-full border border-transparent bg-input-background px-4 text-left text-sm text-foreground transition-colors",
              "hover:border-input focus-visible:border-hairline-strong focus-visible:ring-2 focus-visible:ring-ring/30 focus-visible:outline-none",
              "disabled:cursor-not-allowed disabled:opacity-50",
              "aria-invalid:border-destructive aria-invalid:ring-destructive/20",
              clearable && selected && "pr-9",
            )}
          >
            <CalendarDays className="size-4 shrink-0 text-muted-foreground" aria-hidden />
            <span className={cn("truncate", !selected && "text-muted-foreground")}>
              {selected ? format(selected, "d MMM yyyy") : placeholder}
            </span>
          </button>
        </PopoverTrigger>
        {clearable && selected && !disabled && (
          <button
            type="button"
            onClick={() => onChange("")}
            aria-label="Clear date"
            className="absolute top-1/2 right-2 flex size-5 -translate-y-1/2 items-center justify-center rounded-xs text-muted-foreground transition-colors hover:text-foreground"
          >
            <X className="size-3.5" aria-hidden />
          </button>
        )}
      </div>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selected}
          defaultMonth={selected ?? maxDate ?? minDate}
          onSelect={(d) => {
            onChange(d ? format(d, ISO) : "");
            setOpen(false);
          }}
          disabled={disabledDays}
          captionLayout={captionLayout}
          startMonth={captionLayout === "dropdown" ? new Date(1950, 0) : undefined}
          endMonth={
            captionLayout === "dropdown"
              ? (maxDate ?? new Date(new Date().getFullYear() + 5, 11))
              : undefined
          }
          autoFocus
        />
      </PopoverContent>
    </Popover>
  );
}
